// Industry-specific wording for the built-in promotional campaign templates (Email, Push, WhatsApp).
//
// A salon's industry comes from salons/{salonId}.industry ('hair' | 'clinic' | 'nail' | 'spa'); salons that
// predate the field fall back to INDUSTRY_BY_SALON_ID, then to 'hair'. Only the wording of the built-in
// promotional templates changes - ids, targeting and everything else stay as defined in each editor.
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

export const INDUSTRIES = ['hair', 'clinic', 'nail', 'spa'];

const INDUSTRY_BY_SALON_ID = {
  'vinnwong-hair-studio': 'hair',
  'demo-salon': 'hair',
  'the-scene': 'hair',
  'demo-clinic': 'clinic',
  'demo-nail': 'nail',
  'demo-spa': 'spa'
};

export const resolveIndustry = (salonId, salonData) => {
  const declared = salonData?.industry;
  if (INDUSTRIES.includes(declared)) return declared;
  return INDUSTRY_BY_SALON_ID[salonId] || 'hair';
};

export const getSalonIndustry = async (salonId) => {
  if (!salonId) return 'hair';
  try {
    const snap = await getDoc(doc(db, 'salons', salonId));
    return resolveIndustry(salonId, snap.exists() ? snap.data() : null);
  } catch (error) {
    console.error('Error fetching salon industry:', error);
    return resolveIndustry(salonId, null);
  }
};

// What the business calls the person who looks after the customer
export const TEAM_NOUN = { hair: 'stylist', clinic: 'therapist', nail: 'nail technician', spa: 'therapist' };

const textToHtml = (text) =>
  text.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('\n');

// ── Email ────────────────────────────────────────────────────────────────────
const EMAIL = {
  hair: {
    monthly_special: {
      title: "This Month's Special: 20% Off Colour & Treatments!",
      subject: "This Month's Special: 20% Off Colour & Treatments! ✨",
      body: `Hi {name}!

🎉 THIS MONTH ONLY: 20% OFF COLOUR & TREATMENTS! 🎉

Treat your hair to something special and save 20%:

✨ Full Colour & Highlights
💇‍♀️ Balayage & Colour Touch-Ups
🌿 Keratin & Scalp Treatments
💆‍♀️ Precision Cuts, Wash & Blow

Book your appointment with {salonName} today - slots fill up fast.

Valid through the end of the month. Cannot be combined with other offers.`
    },
    birthday_celebration: {
      subject: 'Happy Birthday! Your Special Gift Awaits 🎂',
      body: `Happy Birthday {name}! 🎂🎉

It's your special month, and we want to celebrate with you!

🎁 YOUR BIRTHDAY GIFT: 25% OFF ANY SERVICE! 🎁

Choose a fresh cut, a new colour or a restorative treatment and enjoy your birthday discount throughout your birthday month.

Book your birthday appointment with {salonName} today - you deserve it!

Valid throughout your birthday month.`
    },
    referral_rewards: {
      body: `Hi {name}!

Love your hair? Share the love and get rewarded!

👯‍♀️ OUR REFERRAL PROGRAMME 👯‍♀️

Refer a friend and you BOTH get $20 off your next cut or colour.

How it works:
1. Tell your friend to quote your name, {name}, when they book their first appointment
2. They enjoy $20 off their first visit
3. You receive $20 off your next visit

The more friends you refer, the more you save!`
    }
  },
  clinic: {
    monthly_special: {
      title: "This Month's Special: 20% Off Facial Treatments!",
      subject: "This Month's Special: 20% Off Facial Treatments! ✨",
      body: `Hi {name}!

🎉 THIS MONTH ONLY: 20% OFF FACIAL TREATMENTS! 🎉

Give your skin the care it deserves and save 20%:

✨ Frozen Age
💧 3D Hydra Reversal
⬆️ Ultralift
🌟 Miracle Oxy Light

Book your treatment with {salonName} today - consultation slots are limited.

Valid through the end of the month. Cannot be combined with other offers.`
    },
    birthday_celebration: {
      subject: 'Happy Birthday! Your Skin Gift Awaits 🎂',
      body: `Happy Birthday {name}! 🎂🎉

It's your special month, and we want to celebrate with you!

🎁 YOUR BIRTHDAY GIFT: 15% OFF ANY FACIAL TREATMENT! 🎁

Treat your skin to Frozen Age, 3D Hydra Reversal, Ultralift or Miracle Oxy Light and enjoy your birthday discount throughout your birthday month.

Book your birthday treatment with {salonName} today - your skin will thank you!

Valid throughout your birthday month.`
    },
    referral_rewards: {
      body: `Hi {name}!

Happy with your results? Share them and get rewarded!

👯‍♀️ OUR REFERRAL PROGRAMME 👯‍♀️

Refer a friend and you BOTH get $20 off your next facial treatment.

How it works:
1. Tell your friend to quote your name, {name}, when they book their first treatment
2. They enjoy $20 off their first visit
3. You receive $20 off your next visit

The more friends you refer, the more you save!`
    }
  },
  nail: {
    monthly_special: {
      title: "This Month's Special: 20% Off Manicures & Pedicures!",
      subject: "This Month's Special: 20% Off Manicures & Pedicures! 💅",
      body: `Hi {name}!

🎉 THIS MONTH ONLY: 20% OFF MANICURES & PEDICURES! 🎉

Treat your hands and feet and save 20%:

💅 Classic Manicure
✨ Gel Manicure
🎨 Nail Art Design
🦶 Deluxe Pedicure

Book your appointment with {salonName} today - weekend slots go quickly.

Valid through the end of the month. Cannot be combined with other offers.`
    },
    birthday_celebration: {
      subject: 'Happy Birthday! Your Special Gift Awaits 🎂',
      body: `Happy Birthday {name}! 🎂🎉

It's your special month, and we want to celebrate with you!

🎁 YOUR BIRTHDAY GIFT: FREE NAIL ART WITH ANY GEL MANICURE! 🎁

Pick your favourite colour, add some sparkle on us, and enjoy your treat any time during your birthday month.

Book your birthday appointment with {salonName} today - you deserve it!

Valid throughout your birthday month.`
    },
    referral_rewards: {
      body: `Hi {name}!

Love your nails? Share the love and get rewarded!

👯‍♀️ OUR REFERRAL PROGRAMME 👯‍♀️

Refer a friend and you BOTH get $10 off your next manicure or pedicure.

How it works:
1. Tell your friend to quote your name, {name}, when they book their first appointment
2. They enjoy $10 off their first visit
3. You receive $10 off your next visit

The more friends you refer, the more you save!`
    }
  },
  spa: {
    monthly_special: {
      title: "This Month's Special: 20% Off Massages!",
      subject: "This Month's Special: 20% Off Massages! 💆",
      body: `Hi {name}!

🎉 THIS MONTH ONLY: 20% OFF ALL MASSAGES! 🎉

Unwind and save 20%:

💆 Swedish Massage
💪 Deep Tissue Massage
🌿 Aromatherapy Massage
🔥 Hot Stone Massage

Book your session with {salonName} today - evening slots are limited.

Valid through the end of the month. Cannot be combined with other offers.`
    },
    birthday_celebration: {
      subject: 'Happy Birthday! Your Relaxation Gift Awaits 🎂',
      body: `Happy Birthday {name}! 🎂🎉

It's your special month, and we want to celebrate with you!

🎁 YOUR BIRTHDAY GIFT: 20% OFF ANY MASSAGE! 🎁

Choose from Swedish, Deep Tissue, Aromatherapy or Hot Stone and enjoy your birthday discount throughout your birthday month.

Book your birthday session with {salonName} today - you deserve a break!

Valid throughout your birthday month.`
    },
    referral_rewards: {
      body: `Hi {name}!

Feeling relaxed? Share the calm and get rewarded!

👯‍♀️ OUR REFERRAL PROGRAMME 👯‍♀️

Refer a friend and you BOTH get $15 off your next massage.

How it works:
1. Tell your friend to quote your name, {name}, when they book their first session
2. They enjoy $15 off their first visit
3. You receive $15 off your next visit

The more friends you refer, the more you save!`
    }
  }
};

// ── Push ─────────────────────────────────────────────────────────────────────
// new_service carries [treatment name]: the admin must replace it with the actual new service before sending
const PUSH = {
  hair: {
    flash_sale: { subject: 'Flash Sale: 30% Off Colour Today Only! ⚡', body: 'Limited time! Book a colour or treatment today and save 30%' },
    birthday_greeting: { subject: 'Happy Birthday! 🎉', body: 'Celebrate with 25% off any hair service this month!' },
    new_service: { subject: 'New Treatment Available! ✨', body: 'Try our new [treatment name] - first visit 20% off!' }
  },
  clinic: {
    flash_sale: { subject: 'Flash Sale: 30% Off Facials Today Only! ⚡', body: 'Limited time! Book a facial treatment today and save 30%' },
    birthday_greeting: { subject: 'Happy Birthday! 🎉', body: 'Celebrate with 15% off any facial treatment this month!' },
    new_service: { subject: 'New Treatment Available! ✨', body: 'Try our new [treatment name] - first session 20% off!' }
  },
  nail: {
    flash_sale: { subject: 'Flash Sale: 30% Off Manicures Today Only! ⚡', body: 'Limited time! Book a manicure or pedicure today and save 30%' },
    birthday_greeting: { subject: 'Happy Birthday! 🎉', body: 'Celebrate with free Nail Art on any Gel Manicure this month!' },
    new_service: { subject: 'New Nail Art Available! ✨', body: 'Try our new [nail art style] - first set 20% off!' }
  },
  spa: {
    flash_sale: { subject: 'Flash Sale: 30% Off Massages Today Only! ⚡', body: 'Limited time! Book a massage today and save 30%' },
    birthday_greeting: { subject: 'Happy Birthday! 🎉', body: 'Celebrate with 20% off any massage this month!' },
    new_service: { subject: 'New Massage Available! ✨', body: 'Try our new [massage name] - first session 20% off!' }
  }
};

// ── WhatsApp (free-form text templates; Meta templates take admin-typed descriptions) ──
const WHATSAPP = {
  hair: {
    weekend_special: {
      subject: 'Weekend Special: 20% Off Hair Colouring! 🎨',
      body: '🎉 Special Weekend Offer! Get 20% off all hair colouring services this Saturday & Sunday only! Book now by replying to this message or calling us at {phone}. Limited slots available! ✨'
    },
    birthday_special: {
      body: '🎂 Happy Birthday Month, {name}! Enjoy a special birthday treat: 30% off any hair service of your choice! Valid throughout your birthday month. Book your celebration appointment today! 🎉'
    },
    referral_program: {
      body: 'Love your hair? Share the love! 💗 Refer a friend and you both get $20 off your next cut or colour. The more you refer, the more you save! Start referring today! 🎁'
    }
  },
  clinic: {
    weekend_special: {
      subject: 'Weekend Special: 20% Off Facial Treatments! ✨',
      body: '🎉 Special Weekend Offer! Get 20% off Frozen Age, 3D Hydra Reversal, Ultralift and Miracle Oxy Light this Saturday & Sunday only! Book now by replying to this message or calling us at {phone}. Limited slots available! ✨'
    },
    birthday_special: {
      body: '🎂 Happy Birthday Month, {name}! Enjoy a birthday gift for your skin: 15% off any facial treatment! Valid throughout your birthday month. Book your treatment today! 🎉'
    },
    referral_program: {
      body: 'Happy with your results? Share them! 💗 Refer a friend and you both get $20 off your next facial treatment. The more you refer, the more you save! Start referring today! 🎁'
    }
  },
  nail: {
    weekend_special: {
      subject: 'Weekend Special: 20% Off Gel Manicures! 💅',
      body: '🎉 Special Weekend Offer! Get 20% off Gel Manicures and Nail Art this Saturday & Sunday only! Book now by replying to this message or calling us at {phone}. Limited slots available! ✨'
    },
    birthday_special: {
      body: '🎂 Happy Birthday Month, {name}! Enjoy free Nail Art with any Gel Manicure! Valid throughout your birthday month. Book your birthday set today! 🎉'
    },
    referral_program: {
      body: 'Love your nails? Share the love! 💗 Refer a friend and you both get $10 off your next manicure or pedicure. The more you refer, the more you save! Start referring today! 🎁'
    }
  },
  spa: {
    weekend_special: {
      subject: 'Weekend Special: 20% Off Massages! 💆',
      body: '🎉 Special Weekend Offer! Get 20% off Aromatherapy and Hot Stone Massages this Saturday & Sunday only! Book now by replying to this message or calling us at {phone}. Limited slots available! ✨'
    },
    birthday_special: {
      body: '🎂 Happy Birthday Month, {name}! Enjoy 20% off any massage! Valid throughout your birthday month. Book your birthday session today! 🎉'
    },
    referral_program: {
      body: 'Feeling relaxed? Share the calm! 💗 Refer a friend and you both get $15 off your next massage. The more you refer, the more you save! Start referring today! 🎁'
    }
  }
};

const CONTENT = { email: EMAIL, push: PUSH, whatsapp: WHATSAPP };

/**
 * Return the built-in templates with industry-specific wording applied to the promotional ones.
 * Custom (admin-saved) templates must not be passed through this - they keep the admin's own wording.
 * @param {'email'|'push'|'whatsapp'} channel
 * @param {Array} templates - built-in templates for one category
 * @param {string} industry
 */
export const applyIndustryContent = (channel, templates, industry) => {
  const overrides = CONTENT[channel]?.[industry] || {};
  return templates.map((t) => {
    const o = t.messageCategory === 'promotional' ? overrides[t.id] : null;
    if (!o) return t;
    const merged = { ...t, ...o };
    if (o.subject && !o.title) merged.title = o.subject.replace(/\s*\p{Extended_Pictographic}+️?\s*$/u, '');
    if (channel === 'email' && o.body) merged.html = textToHtml(o.body);
    return merged;
  });
};
